import { PlanningCreateStatus } from "../enums/planning-create-status";
import { ProductionJobStatus } from "../enums/production-job-status";
import { ProductionJobType } from "../enums/production-job-type";

export enum CellType {
    Property,
    Metric
}

export class CellBase {

    public cellType : CellType;

    // A generic value property that can store some data
    public value : any;
    public parentRunId : number|string;
    public isCurrentlySelected : boolean = false;

    constructor(type : CellType) {
        this.cellType = type;
    }
    
}

export class CellProperty extends CellBase {
    constructor() {
        super(CellType.Property);
    }
}

export class CellMetric extends CellBase {

    // Metrics
    public jobType : ProductionJobType|undefined = undefined;
    public quantity : number = 0;
    public status : ProductionJobStatus = ProductionJobStatus.Unknown;
    
    constructor(jobType: ProductionJobType) {
        super(CellType.Metric);
        this.jobType = jobType;
    }
}

export class MetricBase {

    public isChecked : boolean = false;
    public tmpClientId : string = '';
    public isLoading : boolean = false;
    public createSuccess : PlanningCreateStatus = PlanningCreateStatus.Pending;
    public errorReason : string = '';
    public rank : string = '';

    // Subassembly

    // Subassembly 2283
    public METRIC_SA_2283_AW : CellMetric   = new CellMetric(ProductionJobType.SectionSubassembly83AccentWindows);
    public METRIC_SA_2283_AD : CellMetric   = new CellMetric(ProductionJobType.SectionSubassembly83AccentDoors);
    public METRIC_SA_2283_W : CellMetric    = new CellMetric(ProductionJobType.SectionSubassembly83Windows);
    public METRIC_SA_2283_OW : CellMetric   = new CellMetric(ProductionJobType.SectionSubassembly83OverlayWindows);
    public METRIC_SA_2283_OD : CellMetric   = new CellMetric(ProductionJobType.SectionSubassembly83OverlayDoors);

    // Subassembly Rib
    public METRIC_SA_RIB_W: CellMetric      = new CellMetric(ProductionJobType.SectionSubassemblyRibWindows);

    public METRIC_SA_FR_90 : CellMetric     = new CellMetric(ProductionJobType.SectionSubassemblyFR90);

    public METRIC_SA_FIP : CellMetric       = new CellMetric(ProductionJobType.SectionSubassemblyFIP);
    
    // Print
    public METRIC_PRINT_W: CellMetric       = new CellMetric(ProductionJobType.SectionPrintWindows);
    public METRIC_PRINT_D: CellMetric       = new CellMetric(ProductionJobType.SectionPrintDoors);
    
    // Windows
    public METRIC_WIN_RP : CellMetric       = new CellMetric(ProductionJobType.WindowCutoutRaisedPanel);
    public METRIC_WIN_83 : CellMetric       = new CellMetric(ProductionJobType.WindowCutoutEightyThree);
    public METRIC_WIN_FP : CellMetric       = new CellMetric(ProductionJobType.WindowCutoutFoamPack);
    public METRIC_WIN_AP : CellMetric       = new CellMetric(ProductionJobType.WindowCutoutAccentPack);
    public METRIC_WIN_RIB : CellMetric      = new CellMetric(ProductionJobType.WindowCutoutRib);

    // Section Assembly
    public METRIC_ASM_RP : CellMetric       = new CellMetric(ProductionJobType.SectionAssemblyRP);
    public METRIC_ASM_83 : CellMetric       = new CellMetric(ProductionJobType.SectionAssembly83);
    public METRIC_ASM_FP : CellMetric       = new CellMetric(ProductionJobType.SectionAssemblyFP);
    public METRIC_ASM_AP : CellMetric       = new CellMetric(ProductionJobType.SectionAssemblyAP);
    public METRIC_ASM_RB : CellMetric       = new CellMetric(ProductionJobType.SectionAssemblyRB);
    public METRIC_ASM_FV : CellMetric       = new CellMetric(ProductionJobType.SectionAssemblyFV);
    public METRIC_ASM_CH : CellMetric       = new CellMetric(ProductionJobType.SectionAssemblyCH);
    public METRIC_ASM_I : CellMetric        = new CellMetric(ProductionJobType.SectionAssemblyI);

    // Section Loading
    public METRIC_LOAD_SEC : CellMetric     = new CellMetric(ProductionJobType.SectionLoading);

    // Hardawre Subassembly
    public METRIC_TR_RES : CellMetric       = new CellMetric(ProductionJobType.TrackSubassemblyResidential);
    public METRIC_TR_COMM : CellMetric      = new CellMetric(ProductionJobType.TrackSubassemblyCommercial);
    public METRIC_TR_LHR : CellMetric       = new CellMetric(ProductionJobType.TrackSubassemblyLHR);

    public METRIC_SPRINGS_RES : CellMetric  = new CellMetric(ProductionJobType.SpringSubassemblyResidential);
    public METRIC_SPRINGS_COMM : CellMetric = new CellMetric(ProductionJobType.SpringSubassemblyCommercial);
    public METRIC_STRUTS : CellMetric       = new CellMetric(ProductionJobType.StrutSubassembly);
    public METRIC_HW : CellMetric           = new CellMetric(ProductionJobType.HardwareSubassembly);

    // Needed for building the table
    public METRIC_SPACER : CellMetric       = new CellMetric(ProductionJobType.NotApplicable);

    public METRIC_LIST : CellBase[] = [
        // SA 2283
        this.METRIC_SA_2283_W,
        this.METRIC_SA_2283_OW,
        this.METRIC_SA_2283_OD,
        this.METRIC_SA_FR_90,
        // SA Rib
        this.METRIC_SA_RIB_W,
        // SA Foam Room
        this.METRIC_SA_FIP,
        // Print
        this.METRIC_PRINT_W,
        this.METRIC_PRINT_D,
        // Windows
        this.METRIC_WIN_RP,
        this.METRIC_WIN_83,
        this.METRIC_WIN_FP,
        this.METRIC_WIN_AP,
        this.METRIC_WIN_RIB,
        // Section Assembly
        this.METRIC_ASM_RP,
        this.METRIC_ASM_83,
        this.METRIC_ASM_FP,
        this.METRIC_ASM_AP,
        this.METRIC_ASM_RB,
        this.METRIC_ASM_FV,
        this.METRIC_ASM_CH,
        this.METRIC_ASM_I,

        // Section Loading
        this.METRIC_LOAD_SEC,
        this.METRIC_SPACER,

        // Hardawre Subassembly
        this.METRIC_TR_RES,
        this.METRIC_TR_COMM,
        this.METRIC_TR_LHR,
        this.METRIC_SPRINGS_RES,
        this.METRIC_SPRINGS_COMM,
        this.METRIC_STRUTS,
        this.METRIC_HW
    ];

}
